import type ApplicationModel from 'components/framework/ApplicationModel';
import {
  BookmarksTeamBacklog,
  BookmarksWorkflowBoard,
  type Feature,
  Iteration,
  Project,
  type Requirement,
} from 'components/framework/models';
import { type HasRecordPositions } from 'components/framework/models/concerns/HasRecordPositions';
import { RecordPlacementEnum } from '../../../framework/Enums';
import RecordAction from '../RecordAction';

type Record = (Feature | Requirement) & HasRecordPositions;
export class MoveToPrioritizedBacklogAction extends RecordAction<Record> {
  recordTypes = ['Feature', 'Requirement'];

  get title() {
    return 'Move to prioritized backlog';
  }

  showForRecord(record: ApplicationModel): record is Record {
    const baseCheck =
      super.showForRecord(record) &&
      window.currentProject?.backlogManagementEnabled &&
      window.currentProject?.isTeam &&
      !record.teamWorkflowStatus?.isCompleted &&
      record.team?.id;

    if (!baseCheck) {
      return false;
    }

    const iterationsEnabled = window.currentProject?.iterationsEnabled;

    // If the record is in a sprint, it qualifies
    if (iterationsEnabled && record.iteration) {
      return true;
    }

    const hasWorkflowBoardPosition = record.recordPosition(
      BookmarksWorkflowBoard.bookmarkName
    );
    // If the record is on the workflow board, it qualifies
    if (hasWorkflowBoardPosition && !iterationsEnabled) {
      return true;
    }

    const hasTeamBacklogPosition = record.recordPosition(
      BookmarksTeamBacklog.bookmarkName
    );

    // Record could be in engineering / product backlog
    return !hasTeamBacklogPosition;
  }

  async runForRecords(records: Record[]) {
    if (!window.currentProject?.id) {
      return;
    }

    const project = await Project.select('backlogBookmarkId')
      .merge({ workflowBoardBookmark: ['id'] })
      .find(window.currentProject?.id);

    if (!project) {
      return;
    }

    await Promise.all(
      records.map(async record => {
        await Promise.all([
          record.setRecordPosition({
            bookmarkType: BookmarksTeamBacklog.bookmarkName,
            bookmarkId: project.backlogBookmarkId as RecordId,
            secondaryScopeId: project.backlogBookmarkId as RecordId,
            placement: RecordPlacementEnum.BOTTOM.value,
          }),
          record.removeRecordPosition({
            bookmarkType: BookmarksWorkflowBoard.bookmarkName,
            bookmarkId: project.workflowBoardBookmark?.id as RecordId,
            all: true,
          }),
        ]);

        return record.update(
          { iteration: Iteration.blank() },
          { args: { __reactivelyUpdate: true } }
        );
      })
    );
  }
}
